'use client'

import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui'
import { BugIcon } from 'lucide-react'
import { Log } from '../lib/log'

interface ErrorBoundaryProps {
  children: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * Custom error boundary component that specifically logs Next.js client-side exceptions
 * to Datadog for better tracking and visibility.
 */
export class NextErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      errorInfo: null
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Determine the error source based on the error message
    const errorSource = this.determineErrorSource(error)
    
    // Log using the common logger - this will use console.error until the real logger is loaded
    Log.error(
      'Error Boundary Caught Exception', 
      error, 
      errorSource,
      {
        componentStack: errorInfo.componentStack,
        reactPhase: 'render',
        capturedBy: 'NextErrorBoundary'
      }
    )
  }
  
  /**
   * Helper method to determine the most appropriate error source
   */
  private determineErrorSource(error: Error): 'next_client_exception' | 'next_hydration_error' | 'react_error' {
    const message = error.message.toLowerCase()
    
    if (message.includes('client-side exception')) {
      return 'next_client_exception'
    }
    
    if (message.includes('async/await is not yet supported')) {
      return 'next_client_exception'
    }
    
    if (message.includes('hydration') || message.includes('text content did not match')) {
      return 'next_hydration_error'
    }
    
    return 'react_error'
  }

  render() {
    // Only capture the error - we don't want to render a custom UI here
    // as Next.js will handle the error display
    if (this.state.hasError) {
      // We intentionally don't render a custom fallback UI because we want Next.js to show its error page
      // Just pass through the children to allow Next.js error handling to work
      return this.props.children
    }

    return this.props.children
  }
} 